/* ============================================================
   Experimental, FRONTEND-ONLY analytics for a single GDELT
   cluster's reporting evolution. Every function is pure and
   O(n) over the cluster's member articles (capped ~1000 by the
   API), so it runs comfortably in a memo on the UI thread — no
   backend, no precomputation. The only per-article time signal
   is `publish_time`, so "cases" here means *reporting volume*
   (articles), and the nowcast is an explicitly-labeled estimate.

   Trends are estimated on daily *incidence* (new articles/day), not the
   cumulative curve; direction is gated by an exact two-Poisson significance
   test; and the nowcast is a Gamma–Poisson update whose prior follows the
   recent trajectory (so a declining cluster projects down, not flat).
   ============================================================ */
import { dayjsUTC } from "../../utils/dates"
import type { GdeltClusterMemberArticle } from "./gdeltClusterMemberTypes"

const DAY_MS = 86_400_000

/** Inclusive epoch-ms window. `null` ends fall back to the data extent. */
export interface TimeRange {
  start: number | null
  end: number | null
}

const parseMs = (value: string | null | undefined): number | null => {
  if (!value) return null
  const ms = dayjsUTC(value).valueOf()
  return Number.isFinite(ms) ? ms : null
}

const inRange = (t: number, range: TimeRange): boolean =>
  (range.start == null || t >= range.start) &&
  (range.end == null || t <= range.end)

const startOfUtcDay = (ms: number): number =>
  dayjsUTC(ms).startOf("day").valueOf()

/* ---------- arrival timeline (deduped by timestamp) ---------- */

export interface ArrivalArticle {
  title: string
  sourceName: string
  url: string | null
}

export interface ArrivalNode {
  /** Epoch ms of this exact publish time. */
  t: number
  /** Number of articles published at this exact timestamp. */
  count: number
  articles: ArrivalArticle[]
}

/**
 * One node per *distinct* publish time. Articles sharing an exact timestamp
 * collapse into a single node (the timeline dot) and ride along in `articles`
 * for the hover tooltip. Sorted oldest → newest.
 */
export const dedupeArrivalsByTimestamp = (
  members: GdeltClusterMemberArticle[],
  range: TimeRange = { start: null, end: null }
): ArrivalNode[] => {
  const byTime = new Map<number, ArrivalNode>()
  for (const member of members) {
    const t = parseMs(member.publish_time)
    if (t == null || !inRange(t, range)) continue
    const node = byTime.get(t)
    const article: ArrivalArticle = {
      title: member.title?.trim() || "Untitled",
      sourceName: member.source_name?.trim() || "Unknown",
      url: member.url ?? null,
    }
    if (node) {
      node.count += 1
      node.articles.push(article)
    } else {
      byTime.set(t, { t, count: 1, articles: [article] })
    }
  }
  return Array.from(byTime.values()).sort((a, b) => a.t - b.t)
}

/* ---------- daily buckets (gap-filled, cumulative) ---------- */

export interface ArrivalBucket {
  /** Epoch ms at the start of the UTC day. */
  t: number
  count: number
  cumulative: number
}

/** Cap so a pathological multi-year span can't allocate an unbounded array. */
const MAX_BUCKETS = 740

/**
 * Daily article counts across the range, including zero-count days so the
 * cumulative curve and rate trend are continuous (a fit over a sparse series
 * would otherwise be skewed). Range defaults to the data extent.
 */
export const bucketArrivals = (
  members: GdeltClusterMemberArticle[],
  range: TimeRange = { start: null, end: null }
): ArrivalBucket[] => {
  const times: number[] = []
  for (const member of members) {
    const t = parseMs(member.publish_time)
    if (t != null && inRange(t, range)) times.push(t)
  }
  if (times.length === 0) return []

  const dataMin = Math.min(...times)
  const dataMax = Math.max(...times)
  const firstDay = startOfUtcDay(range.start ?? dataMin)
  const lastDay = startOfUtcDay(range.end ?? dataMax)

  const counts = new Map<number, number>()
  for (const t of times) {
    const day = startOfUtcDay(t)
    counts.set(day, (counts.get(day) ?? 0) + 1)
  }

  const totalDays = Math.floor((lastDay - firstDay) / DAY_MS) + 1
  const dayCount = Math.min(Math.max(totalDays, 1), MAX_BUCKETS)
  // Anchor the window to the MOST RECENT day. When the span exceeds MAX_BUCKETS
  // we drop the oldest days, never the recent tail that the trend and nowcast
  // depend on. Counts before the window still seed the cumulative baseline so
  // `cumulative` stays a true running total.
  const windowStart = lastDay - (dayCount - 1) * DAY_MS
  let cumulative = 0
  for (const [day, c] of counts) {
    if (day < windowStart) cumulative += c
  }
  const buckets: ArrivalBucket[] = []
  for (let i = 0; i < dayCount; i += 1) {
    const t = windowStart + i * DAY_MS
    cumulative += counts.get(t) ?? 0
    buckets.push({ t, count: counts.get(t) ?? 0, cumulative })
  }
  return buckets
}

/* ---------- trend classification ---------- */

export type TrendKind =
  | "accelerating"
  | "linear"
  | "decelerating"
  | "insufficient"

export interface TrendResult {
  kind: TrendKind
  /** late-window vs early-window mean daily rate, minus 1 (0 = flat). */
  curvature: number
  /** Ratio of late-window to early-window mean daily rate. */
  recentVsEarlyRatio: number
  /** Fitted exponential growth rate per day (k in N ∝ e^{kt}). */
  growthRatePerDay: number
  /** Doubling time in days when growing, else null. */
  doublingDays: number | null
  /** R² of the log-linear fit to *daily incidence* (not cumulative). */
  rSquared: number
  /**
   * Two-sided exact p-value that the early- and late-window inflow rates differ
   * (conditional two-Poisson / binomial test). A direction is only declared when
   * this clears ALPHA — so the rate change must be statistically distinguishable
   * from sampling noise, not merely cross a fixed ratio.
   */
  pValue: number
}

const MIN_BUCKETS = 4
const MIN_ARTICLES = 6
const ACCEL_RATIO = 1.3
const DECEL_RATIO = 0.75
/** Significance level the rate-change test must clear to declare a direction. */
const TREND_ALPHA = 0.05

interface Fit {
  slope: number
  intercept: number
  r2: number
}

/**
 * Ordinary least squares of `ys` against their index (x = 0, 1, 2, …), with the
 * coefficient of determination. Every series we fit here is evenly spaced by
 * day, so the index IS the x-axis.
 */
const linregress = (ys: number[]): Fit => {
  const n = ys.length
  if (n < 2) return { slope: 0, intercept: ys[0] ?? 0, r2: 0 }
  let sx = 0
  let sy = 0
  let sxx = 0
  let sxy = 0
  for (let i = 0; i < n; i += 1) {
    const y = ys[i] ?? 0
    sx += i
    sy += y
    sxx += i * i
    sxy += i * y
  }
  const denom = n * sxx - sx * sx
  if (denom === 0) return { slope: 0, intercept: sy / n, r2: 0 }
  const slope = (n * sxy - sx * sy) / denom
  const intercept = (sy - slope * sx) / n
  const meanY = sy / n
  let ssTot = 0
  let ssRes = 0
  for (let i = 0; i < n; i += 1) {
    const y = ys[i] ?? 0
    const pred = slope * i + intercept
    ssTot += (y - meanY) ** 2
    ssRes += (y - pred) ** 2
  }
  const r2 = ssTot === 0 ? 1 : Math.max(0, 1 - ssRes / ssTot)
  return { slope, intercept, r2 }
}

/* ---------- exact two-Poisson rate-change test ---------- */

/**
 * ln Γ(z) via the Stirling series, up-shifting small z (Γ(z) = Γ(z+1)/z) so the
 * asymptotic tail is tiny. Accurate to ~1e-10 for z ≥ 1, which covers every
 * call here — arguments are always integer+1. Uses only exact-fraction
 * constants, so no float-precision loss.
 */
const lnGamma = (z: number): number => {
  let x = z
  let shift = 0
  while (x < 8) {
    shift += Math.log(x)
    x += 1
  }
  const inv = 1 / x
  const series =
    (x - 0.5) * Math.log(x) -
    x +
    0.5 * Math.log(2 * Math.PI) +
    inv / 12 -
    inv ** 3 / 360 +
    inv ** 5 / 1260
  return series - shift
}

const lnChoose = (n: number, k: number): number =>
  lnGamma(n + 1) - lnGamma(k + 1) - lnGamma(n - k + 1)

const binomLogPmf = (k: number, n: number, p: number): number => {
  if (p <= 0) return k === 0 ? 0 : -Infinity
  if (p >= 1) return k === n ? 0 : -Infinity
  return lnChoose(n, k) + k * Math.log(p) + (n - k) * Math.log1p(-p)
}

/**
 * Two-sided exact p-value that `k` successes out of `n` came from rate `p`
 * (minimum-likelihood method: sum the probability of every outcome no more
 * likely than the observed one). Used to test whether the late-window inflow
 * share differs from its expected share under a constant rate — i.e. whether
 * the early-vs-late rate change is distinguishable from sampling noise.
 */
const binomTwoSidedP = (k: number, n: number, p: number): number => {
  if (n <= 0) return 1
  const obs = binomLogPmf(k, n, p)
  if (!Number.isFinite(obs)) return 1
  let total = 0
  for (let i = 0; i <= n; i += 1) {
    const lp = binomLogPmf(i, n, p)
    // 1e-7 slack guards against float jitter when |pmf(i) - pmf(k)| ≈ 0.
    if (lp <= obs + 1e-7) total += Math.exp(lp)
  }
  return Math.min(1, total)
}

/**
 * Classify how a cluster's article inflow is evolving. A direction is declared
 * only when the early-vs-late rate change clears an exact significance test —
 * the conditional two-Poisson (binomial) test — AND meets a practical
 * effect-size floor, so a 1.4× ratio off a handful of articles reads as
 * "steady", not "accelerating". The growth rate and doubling time come from a
 * log-linear fit to *daily incidence* (new articles/day), never to the
 * cumulative curve — fitting cumulative is autocorrelated, lags a current
 * surge, and inflates R². Returns `insufficient` when there isn't enough signal
 * to say anything honest.
 */
export const classifyTrend = (buckets: ArrivalBucket[]): TrendResult => {
  const empty: TrendResult = {
    kind: "insufficient",
    curvature: 0,
    recentVsEarlyRatio: 1,
    growthRatePerDay: 0,
    doublingDays: null,
    rSquared: 0,
    pValue: 1,
  }
  const n = buckets.length
  const total = buckets.reduce((sum, b) => sum + b.count, 0)
  if (n < MIN_BUCKETS || total < MIN_ARTICLES) return empty

  const mid = Math.floor(n / 2)
  const earlyDays = mid
  const lateDays = n - mid
  const earlyCount = buckets.slice(0, mid).reduce((sum, b) => sum + b.count, 0)
  const lateCount = buckets.slice(mid).reduce((sum, b) => sum + b.count, 0)
  const earlyRate = earlyDays > 0 ? earlyCount / earlyDays : 0
  const lateRate = lateDays > 0 ? lateCount / lateDays : 0
  const ratio =
    earlyRate > 0 ? lateRate / earlyRate : lateRate > 0 ? Infinity : 1

  // Under a constant rate, the late window should hold a share `pLate` of the
  // total inflow proportional to its length. Test the observed late count
  // against that expectation — this folds the sample size into the decision,
  // unlike a bare ratio threshold.
  const pLate = lateDays / (earlyDays + lateDays)
  const pValue = binomTwoSidedP(lateCount, earlyCount + lateCount, pLate)
  const significant = pValue < TREND_ALPHA

  // Growth metrics from daily incidence (counts), not cumulative.
  const incFit = linregress(buckets.map(b => Math.log(b.count + 1)))
  const k = incFit.slope
  const doublingDays = k > 1e-9 ? Math.log(2) / k : null

  let kind: TrendKind = "linear"
  if (significant && ratio >= ACCEL_RATIO) kind = "accelerating"
  else if (significant && ratio <= DECEL_RATIO) kind = "decelerating"

  return {
    kind,
    curvature: Number.isFinite(ratio) ? ratio - 1 : 1,
    recentVsEarlyRatio: Number.isFinite(ratio) ? ratio : 999,
    growthRatePerDay: k,
    doublingDays,
    rSquared: incFit.r2,
    pValue,
  }
}

/* ---------- nowcast (projected reporting volume today) ---------- */

export interface NowcastResult {
  /** Reference "now" the projection was made against (epoch ms). */
  asOf: number
  /** Articles already attributed to the current UTC day. */
  observedToday: number
  /** Estimated full-day reporting volume for today. */
  projectedToday: number
  /** Plausible low/high band around the projection. */
  low: number
  high: number
  /** Fraction of the current UTC day elapsed. */
  dayFraction: number
  /**
   * Trend-projected expected full-day rate used as the prior — the recent
   * trajectory extrapolated to today, so a declining series projects DOWN.
   */
  expectedRate: number
  /** True when there isn't enough recent activity to project honestly. */
  insufficient: boolean
}

const RECENT_WINDOW_DAYS = 7
/** Floor the elapsed fraction so the partial-day exposure can't reach zero. */
const MIN_DAY_FRACTION = 1 / 24
/** Beyond this gap since the last article the cluster is treated as stale. */
const STALE_GAP_DAYS = 2
/** Need at least this many recent days before trusting a log-linear trend. */
const MIN_TREND_POINTS = 3
/** Prior strength in days: the prior is worth ~this much observed exposure. */
const PRIOR_STRENGTH_DAYS = 2
/** z for a ~80% central predictive interval on the projected volume. */
const PI_Z = 1.2816

/**
 * Project the expected full-day rate for *today* from the recent completed
 * days. We fit a log-linear (exponential) trend to recent daily incidence and
 * extrapolate one day forward, so a declining cluster projects DOWN toward zero
 * rather than carrying a lagging flat mean. The projection is blended toward
 * the plain mean by how well the trend actually fits (R²) — a noisy series with
 * no real slope falls back to the mean instead of chasing spurious growth — and
 * bounded to twice the recent max so a steep fit can't extrapolate to absurdity.
 */
const projectDailyRate = (recent: ArrivalBucket[]): number => {
  if (recent.length === 0) return 0
  const counts = recent.map(b => b.count)
  const mean = counts.reduce((sum, c) => sum + c, 0) / counts.length
  if (counts.length < MIN_TREND_POINTS) return mean

  const fit = linregress(counts.map(c => Math.log(c + 1)))
  // Indices ran 0…len-1 over the completed days; today is the next index.
  const projected = Math.expm1(fit.slope * counts.length + fit.intercept)
  if (!Number.isFinite(projected)) return mean

  const bounded = Math.min(Math.max(0, projected), 2 * Math.max(...counts))
  return fit.r2 * bounded + (1 - fit.r2) * mean
}

/**
 * Project today's reporting volume, accounting for backdated reporting: the
 * current day is only partially observed, so its raw count under-states the
 * full-day volume. We treat the day's count as Poisson and apply a Gamma–
 * Poisson (conjugate) update:
 *
 *   • the prior is the trend-projected expected rate (`projectDailyRate`),
 *     given a strength of PRIOR_STRENGTH_DAYS days of pseudo-exposure;
 *   • the likelihood is today's `observedToday` over `dayFraction` of exposure.
 *
 * The posterior mean rate therefore leans on the trend prior early in the day
 * and on the observed partial day as it fills in — an exposure-weighted blend,
 * not the old flat 50/50 average of a wild `observed/dayFraction` inflation and
 * a lagging mean. Because the prior follows the recent trajectory, a declining
 * cluster projects toward zero instead of carrying a stale weekly mean. The
 * band is a real ~80% posterior-predictive interval (negative-binomial), and we
 * never report below what's already observed.
 *
 * Still a single-snapshot trend extrapolation, not a true reporting-triangle
 * nowcast (we have no reporting-delay distribution), so callers label it EST.
 */
export const nowcastVolume = (
  buckets: ArrivalBucket[],
  asOf: number
): NowcastResult => {
  const currentDayStart = startOfUtcDay(asOf)
  const todayBucket = buckets.find(b => b.t === currentDayStart)
  const observedToday = todayBucket?.count ?? 0
  const dayFraction = Math.min(
    1,
    Math.max(MIN_DAY_FRACTION, (asOf - currentDayStart) / DAY_MS)
  )

  const completed = buckets.filter(b => b.t < currentDayStart)
  const recent = completed.slice(-RECENT_WINDOW_DAYS)
  const expectedRate = projectDailyRate(recent)

  const base: NowcastResult = {
    asOf,
    observedToday,
    projectedToday: observedToday,
    low: observedToday,
    high: observedToday,
    dayFraction,
    expectedRate,
    insufficient: true,
  }
  const lastBucket = buckets[buckets.length - 1]
  if (!lastBucket) return base

  // Stale: nothing recent and nothing today → a projection would be noise.
  const stale = asOf - lastBucket.t > STALE_GAP_DAYS * DAY_MS
  if (
    (stale && observedToday === 0) ||
    (observedToday === 0 && expectedRate <= 0)
  ) {
    return base
  }

  // Gamma–Poisson conjugate update. Prior λ ~ Gamma(a0, b0) with rate-parameter
  // b0 in units of days, so its mean a0/b0 = expectedRate. After observing
  // `observedToday` over `dayFraction` of exposure the posterior is
  // Gamma(a0 + observedToday, b0 + dayFraction).
  const b0 = PRIOR_STRENGTH_DAYS
  const a0 = expectedRate * b0
  const aPost = a0 + observedToday
  const bPost = b0 + dayFraction
  const posteriorRate = aPost / bPost

  // Expected remaining count over the unobserved rest of the day, and its
  // posterior-predictive (negative-binomial) variance: mean·(1 + exposure/bPost).
  const remainingExposure = 1 - dayFraction
  const remainingMean = posteriorRate * remainingExposure
  const remainingVar = remainingMean * (1 + remainingExposure / bPost)
  const remainingSd = Math.sqrt(Math.max(0, remainingVar))

  const projectedToday = Math.max(
    observedToday,
    Math.round(observedToday + remainingMean)
  )
  const low = Math.max(
    observedToday,
    Math.round(observedToday + Math.max(0, remainingMean - PI_Z * remainingSd))
  )
  const high = Math.max(
    projectedToday,
    Math.round(observedToday + remainingMean + PI_Z * remainingSd)
  )

  return {
    asOf,
    observedToday,
    projectedToday,
    low,
    high,
    dayFraction,
    expectedRate,
    insufficient: false,
  }
}

/* ---------- backtest (leakage-free historical replay) ---------- */

export interface BacktestSample {
  /** UTC day start (epoch ms) being predicted. */
  t: number
  /** True full-day article count for that day. */
  actual: number
  /** Day-start ("cold") projected full-day volume from the nowcast. */
  predicted: number
  /** Predictive-interval bounds the nowcast emitted for that day. */
  low: number
  high: number
  /** Whether `actual` fell within [low, high]. */
  inInterval: boolean
}

export interface BacktestResult {
  /** Number of held-out days evaluated. */
  n: number
  /** Mean absolute error of the trend-aware projection. */
  mae: number
  /** Root mean squared error. */
  rmse: number
  /** Symmetric MAPE (%). Robust to the zero-count days a raw MAPE divides by. */
  smape: number
  /** MAE of the old flat recent-mean rate, kept as a skill baseline. */
  baselineMae: number
  /** 1 − mae/baselineMae. Positive ⇒ the trend prior beats the lagging mean. */
  skill: number
  /** Fraction of actuals that landed inside the predictive interval. */
  coverage: number
  /** Nominal coverage the interval targets (the ~80% PI). */
  targetCoverage: number
  samples: BacktestSample[]
}

/** Need at least this much prior history before a day is predictable. */
const MIN_BACKTEST_HISTORY = MIN_TREND_POINTS
const BACKTEST_TARGET_COVERAGE = 0.8

const EMPTY_BACKTEST: BacktestResult = {
  n: 0,
  mae: 0,
  rmse: 0,
  smape: 0,
  baselineMae: 0,
  skill: 0,
  coverage: 0,
  targetCoverage: BACKTEST_TARGET_COVERAGE,
  samples: [],
}

/**
 * Replay the nowcast over the cluster's own history to measure whether it
 * actually predicts. For each day with enough prior history we stand at the
 * *start* of that day — observing nothing yet, the hardest case — feed the
 * model ONLY the strictly-earlier days (no leakage), and compare its full-day
 * projection to the day's known total. We report point accuracy (MAE / RMSE /
 * symmetric MAPE), interval calibration (how often the actual fell inside the
 * stated ~80% band), and skill against the old flat recent-mean rate, so the
 * trend-aware prior's advantage is measured rather than asserted.
 */
export const backtestNowcast = (buckets: ArrivalBucket[]): BacktestResult => {
  if (buckets.length <= MIN_BACKTEST_HISTORY) return EMPTY_BACKTEST

  const samples: BacktestSample[] = []
  let absSum = 0
  let sqSum = 0
  let smapeSum = 0
  let baseAbsSum = 0
  let inCount = 0

  for (let i = MIN_BACKTEST_HISTORY; i < buckets.length; i += 1) {
    const day = buckets[i]
    if (!day) continue
    const prefix = buckets.slice(0, i)

    // Day-start view: the day under test, observed as empty so far, appended to
    // the strictly-earlier prefix. `asOf` sits 1ms into the day, so the nowcast
    // sees a fresh day and projects purely from prior-day history.
    const view: ArrivalBucket[] = [
      ...prefix,
      { t: day.t, count: 0, cumulative: 0 },
    ]
    const result = nowcastVolume(view, day.t + 1)

    const actual = day.count
    const error = result.projectedToday - actual
    absSum += Math.abs(error)
    sqSum += error * error
    const denom = Math.abs(result.projectedToday) + Math.abs(actual)
    smapeSum += denom === 0 ? 0 : Math.abs(error) / (denom / 2)

    // Baseline: the old flat mean of the same recent window.
    const recent = prefix.slice(-RECENT_WINDOW_DAYS)
    const baseline =
      recent.length > 0
        ? Math.round(
            recent.reduce((sum, b) => sum + b.count, 0) / recent.length
          )
        : 0
    baseAbsSum += Math.abs(baseline - actual)

    const inInterval = actual >= result.low && actual <= result.high
    if (inInterval) inCount += 1

    samples.push({
      t: day.t,
      actual,
      predicted: result.projectedToday,
      low: result.low,
      high: result.high,
      inInterval,
    })
  }

  const n = samples.length
  if (n === 0) return EMPTY_BACKTEST

  const mae = absSum / n
  const baselineMae = baseAbsSum / n
  return {
    n,
    mae,
    rmse: Math.sqrt(sqSum / n),
    smape: (smapeSum / n) * 100,
    baselineMae,
    skill: baselineMae > 0 ? 1 - mae / baselineMae : mae === 0 ? 1 : 0,
    coverage: inCount / n,
    targetCoverage: BACKTEST_TARGET_COVERAGE,
    samples,
  }
}
